/**
 * Client-side product setup coach - applies answers directly to the draft
 * without LLM round-trips or double-confirm chatter.
 */

import { FIELD_DEFS, getProductCompletionStatus } from "./productCompletionGuide";

export { FIELD_DEFS, getProductCompletionStatus };

export const QUICK_REPLIES_BY_KEY = {
  type: ["Standard", "Variant"],
  condition: ["New", "Used", "Refurbished"],
  quantity: ["Unlimited", "10", "25", "50"],
};

const RESERVED_BRAND_NAMES = new Set([
  "new",
  "used",
  "refurbished",
  "standard",
  "variant",
  "unlimited",
  "yes",
  "no",
  "ok",
  "okay",
]);

export function getFieldByKey(key) {
  return FIELD_DEFS.find((f) => f.key === key) || null;
}

export function questionForField(field) {
  if (!field) return "";
  switch (field.key) {
    case "type":
      return "What type of product is this?";
    case "condition":
      return "What condition is it in?";
    case "brand":
      return "Which brand is this?";
    case "price":
      return "What price (excl. tax)?";
    case "taxClass":
      return "Which tax class applies?";
    case "quantity":
      return "How many do you have in stock?";
    case "summary":
      return "Give me a short one-line summary for shoppers.";
    case "name":
      return "What should we call this product?";
    default:
      return field.prompt || `What about ${field.label}?`;
  }
}

export function getQuickRepliesForField(field, helpers = {}) {
  if (!field) return [];
  const preset = QUICK_REPLIES_BY_KEY[field.key];
  if (preset) return preset;
  if (field.key === "brand" && Array.isArray(helpers.brandNames) && helpers.brandNames.length) {
    return helpers.brandNames.slice(0, 5);
  }
  if (field.key === "taxClass" && Array.isArray(helpers.taxClassNames) && helpers.taxClassNames.length) {
    return helpers.taxClassNames.slice(0, 6);
  }
  return [];
}

export function coachDockHint(field, helpers = {}) {
  if (!field) return "";
  switch (field.key) {
    case "brand":
      if (helpers.brandNames?.length) {
        return "Tap one above, or type a different name. KAI will create it if it's new.";
      }
      return "Type a brand name below. KAI will create and attach it.";
    case "price":
      return "Type the price, e.g. 29.99";
    case "taxClass":
      if (!helpers.taxClassNames?.length) {
        return "Type or pick the tax class that applies.";
      }
      return "";
    case "summary":
    case "name":
      return "Type your answer below.";
    default:
      return "";
  }
}

function ciMatch(list, value, key = "name") {
  const v = String(value || "").trim().toLowerCase();
  return (list || []).find((x) => String(x?.[key] || "").toLowerCase() === v) || null;
}

function normalizeBrandInput(raw) {
  return String(raw || "")
    .trim()
    .replace(/^(the\s+)?brand\s*(is|:)\s*/i, "")
    .replace(/^it'?s\s+/i, "")
    .trim();
}

function parsePrice(text) {
  const m = String(text).replace(/[£$€,\s]/g, "").match(/(\d+(?:\.\d{1,2})?)/);
  return m ? Number(m[1]) : NaN;
}

const SKIP_AS_ANSWER = /^(yes|y|no|n|ok|okay|confirm|cancel|nope|sure)$/i;

/** Reject answers that belong to a different field (e.g. "New" as a brand). */
export function validateAnswerForField(field, text) {
  if (!field) return { valid: false, reason: "no-field" };
  const raw = String(text || "").trim();
  if (!raw) return { valid: false, reason: "empty" };
  if (SKIP_AS_ANSWER.test(raw)) return { valid: false, reason: "confirm-word" };

  const lower = raw.toLowerCase();

  if (field.key === "brand") {
    const name = normalizeBrandInput(raw);
    if (!name || name.length < 2) return { valid: false, reason: "too-short" };
    if (RESERVED_BRAND_NAMES.has(name.toLowerCase())) {
      return { valid: false, reason: "reserved-brand" };
    }
    if ((QUICK_REPLIES_BY_KEY.condition || []).some((c) => c.toLowerCase() === lower)) {
      return { valid: false, reason: "looks-like-condition" };
    }
    if ((QUICK_REPLIES_BY_KEY.type || []).some((c) => c.toLowerCase() === lower)) {
      return { valid: false, reason: "looks-like-type" };
    }
    if ((QUICK_REPLIES_BY_KEY.quantity || []).some((c) => c.toLowerCase() === lower)) {
      return { valid: false, reason: "looks-like-quantity" };
    }
    return { valid: true, value: name };
  }

  if (field.key === "type") {
    const name =
      ciMatch(field._helpers?.productTypes, raw)?.name ||
      QUICK_REPLIES_BY_KEY.type.find((t) => t.toLowerCase() === lower);
    return name ? { valid: true, value: name } : { valid: false, reason: "invalid-type" };
  }

  if (field.key === "condition") {
    const name =
      ciMatch(field._helpers?.productConditions, raw)?.name ||
      QUICK_REPLIES_BY_KEY.condition.find((c) => c.toLowerCase() === lower);
    return name ? { valid: true, value: name } : { valid: false, reason: "invalid-condition" };
  }

  if (field.key === "price") {
    const n = parsePrice(raw);
    return Number.isFinite(n) && n >= 0 ? { valid: true, value: n } : { valid: false, reason: "invalid-price" };
  }

  if (field.key === "quantity") {
    if (/unlimited/i.test(raw)) return { valid: true, value: "unlimited" };
    const n = parseInt(raw.replace(/\D/g, ""), 10);
    return Number.isFinite(n) && n >= 0 ? { valid: true, value: n } : { valid: false, reason: "invalid-qty" };
  }

  if (field.key === "taxClass") {
    const tc =
      ciMatch(field._helpers?.taxClasses, raw) || ciMatch(field._helpers?.taxClasses, raw, "id");
    return tc ? { valid: true, value: tc.id } : { valid: false, reason: "invalid-tax" };
  }

  return { valid: true, value: raw };
}

export function buildOptimisticPatch(fieldKey, value, helpers, extras = {}) {
  const draft = extras.draft || {};
  const patch = {};

  switch (fieldKey) {
    case "type": {
      const obj = ciMatch(helpers.productTypes, value);
      patch.productType = obj || { name: String(value) };
      break;
    }
    case "condition": {
      const obj = ciMatch(helpers.productConditions, value);
      patch.productCondition = obj || { name: String(value) };
      break;
    }
    case "brand":
      if (extras.brand) patch.brand = extras.brand;
      break;
    case "price":
      patch.priceInDefaultCurrency = Number(value);
      break;
    case "quantity":
      if (value === "unlimited") {
        patch.isProductInventoryUnlimited = true;
      } else {
        patch.quantityAvailable = Number(value);
        patch.isProductInventoryUnlimited = false;
      }
      break;
    case "taxClass":
      patch.taxClassId = value;
      break;
    case "summary":
      patch.productInformation = {
        ...(draft.productInformation || {}),
        summary: value,
      };
      break;
    case "name":
      patch.productInformation = {
        ...(draft.productInformation || {}),
        name: value,
      };
      break;
    default:
      break;
  }
  return patch;
}

export function mergeDraft(draft, patch) {
  const next = { ...draft };
  if (patch.productInformation) {
    next.productInformation = { ...(draft.productInformation || {}), ...patch.productInformation };
  }
  for (const [k, v] of Object.entries(patch)) {
    if (k !== "productInformation") next[k] = v;
  }
  return next;
}

/**
 * Apply answer for the explicit coach target field (not whatever stale draft says).
 */
export async function tryApplyGuidanceAnswer(text, field, { helpers, applyGuidanceField }) {
  if (!field || !applyGuidanceField) return null;

  const fieldWithHelpers = { ...field, _helpers: helpers };
  const validation = validateAnswerForField(fieldWithHelpers, text);
  if (!validation.valid) return { ok: false, reason: validation.reason };

  return applyGuidanceField(field.key, validation.value);
}

export function validationErrorMessage(reason, field) {
  switch (reason) {
    case "looks-like-condition":
      return "That looks like a condition (New/Used/Refurbished), not a brand. Pick or type a brand name.";
    case "looks-like-type":
      return "That looks like a product type. Use the Standard / Variant buttons for type.";
    case "looks-like-quantity":
      return "That looks like a stock quantity. We'll ask for that next.";
    case "reserved-brand":
      return "Please enter a real brand name (not a product option like \"New\" or \"Standard\").";
    case "invalid-type":
      return "Please pick Standard or Variant.";
    case "invalid-condition":
      return "Please pick New, Used, or Refurbished.";
    case "invalid-price":
      return "Enter a valid price, e.g. 29.99";
    case "invalid-qty":
      return "Enter a quantity or tap Unlimited.";
    case "invalid-tax":
      return `Pick one of the tax classes for ${field?.label || "tax"}.`;
    case "brand-failed":
      return "Couldn't create that brand. Try another name.";
    default:
      return null;
  }
}

export function successMessage(result) {
  const label = result?.label || "Field";
  const value = result?.value != null ? result.value : "";
  const brandNote = result?.createdBrand ? " (added as a new brand)" : "";
  if (result?.appliedFieldKey === "price") {
    const priceText = value || "";
    if (result?.status?.isComplete) {
      return `Thanks for the price! I've set the price to ${priceText} on your draft. All required fields done. Ready to save!`;
    }
    return `Thanks for the price! I've set the price to ${priceText} on your draft.`;
  }
  if (result?.status?.isComplete) {
    return `✓ ${label}: ${value}${brandNote}. All required fields done. Ready to save!`;
  }
  return `✓ ${label}: ${value}${brandNote}`;
}
